import React, { useState } from 'react';
import { Page } from '../../components';
import DhobaraPageLayout from '../../components/DhobaraPageLayout/DhobaraPageLayout';
import css from './FAQPage.module.css';

const FAQ_SECTIONS = [
  {
    title: 'Buying on Dhobara',
    items: [
      {
        q: 'How do I buy something on Dhobara?',
        a: 'Browse our curated listings, select the piece you love, and complete checkout securely. Once your order is confirmed, the seller has 2 business days to ship using our prepaid UPS label.',
      },
      {
        q: 'Are the items authentic?',
        a: 'Yes. All sellers on Dhobara agree to our authenticity terms and warrant that every item listed is 100% genuine. We are actively building pre-authentication into our listing process for additional buyer confidence.',
      },
      {
        q: 'What if the item is not as described?',
        a: 'You have a 3-day inspection window from the date of delivery. If an item is materially misrepresented, contact us at admin@dhobara.com and we will open a return.',
      },
      {
        q: 'Can I return something if I change my mind?',
        a: 'Our return window is for items that are not as described, not for change of mind. Please review listings carefully before purchasing.',
      },
      {
        q: 'How long does shipping take?',
        a: 'Sellers ship within 2 business days of sale. UPS delivers in 3–5 business days from pickup. You\'ll receive tracking information once the item has been shipped.',
      },
    ],
  },
  {
    title: 'Designers & Inventory',
    items: [
      {
        q: 'Which designers do you accept?',
        a: 'We accept over 200 South Asian designers — from Sabyasachi and Manish Malhotra to Élan, Sania Maskatiya, and Nomi Ansari. See our full Designers page for the complete list.',
      },
      {
        q: 'What if my designer is not on the list?',
        a: 'Contact us at admin@dhobara.com. We review the designer list regularly and may be able to accommodate your piece.',
      },
      {
        q: 'What occasions are covered?',
        a: 'We carry bridal wear, formal event pieces, casual luxury, menswear, and kids\' occasion wear. If it was made by a South Asian designer of note, it belongs on Dhobara.',
      },
    ],
  },
  {
    title: 'Pre-Owned vs Custom',
    items: [
      {
        q: 'Can I sell altered or customised pieces?',
        a: 'Yes, as long as the alterations are clearly disclosed in the listing. Any modifications to the original design must be described accurately, including hemming, resizing, or embellishment changes.',
      },
      {
        q: 'What condition grades do you use?',
        a: '<p><strong>New with tags</strong> — Never worn, original tags attached.</p><p><strong>Like new</strong> — Worn once or twice, no visible wear.</p><p><strong>Gently used</strong> — Light signs of wear, no major defects.</p><p><strong>Well loved</strong> — Visible wear, clearly disclosed in listing.</p>',
        isHtml: true,
      },
    ],
  },
  {
    title: 'Selling on Dhobara',
    items: [
      {
        q: 'How do I list an item?',
        a: 'Create an account, complete your seller profile, and submit a listing through your seller dashboard. You\'ll provide photos, condition details, sizing, and your asking price. Listings are reviewed before going live.',
      },
      {
        q: 'How much do I earn?',
        a: 'You keep 80% of the sale price. Dhobara takes 20% to cover listing management, photo enhancement, buyer support, and payment processing. Payouts are issued within 5 business days of the buyer\'s return window closing.',
      },
      {
        q: 'How does shipping work for sellers?',
        a: 'When your item sells, Dhobara sends you a prepaid UPS shipping label. Pack the item securely and drop it off at any UPS location within 2 business days of receiving the label.',
      },
      {
        q: 'Can I list on other platforms at the same time?',
        a: 'No. Our terms require exclusive listing on Dhobara. If your item sells elsewhere, it must be immediately removed from Dhobara.',
      },
      {
        q: 'What happens if a buyer returns my item?',
        a: 'The item is returned to you. If it comes back in a different condition than you shipped it, you have 2 days to raise a dispute with photo evidence at admin@dhobara.com. If no dispute is raised, the buyer refund is processed and your payout is voided. The item will be automatically relisted.',
      },
    ],
  },
  {
    title: 'About Dhobara',
    items: [
      {
        q: 'What does "Dhobara" mean?',
        a: '"Dhobara" means "again" in Urdu and Hindi — as in wearing something again, giving it a second life. "Dhobara pahan lo" — wear it again.',
      },
      {
        q: 'Where are you based?',
        a: 'We are US-based and currently serve the US market only. We are a small team with a big love for South Asian fashion.',
      },
      {
        q: 'How do I contact you?',
        a: 'Email us at admin@dhobara.com or use the Contact page. We aim to respond within 1–2 business days.',
      },
    ],
  },
];

// Strip basic HTML tags so AI answer engines / schema.org consumers get clean
// plain-text answers (this is the AEO/GEO win — lets Google, ChatGPT, Perplexity,
// etc. quote our FAQ content directly as a structured answer).
const stripHtml = html => html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQ_SECTIONS.flatMap(section =>
    section.items.map(item => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.isHtml ? stripHtml(item.a) : item.a,
      },
    }))
  ),
};

const FAQItem = ({ q, a, isHtml }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className={css.item}>
      <button className={css.question} onClick={() => setOpen(o => !o)}>
        {q}
        <span className={`${css.questionIcon} ${open ? css.questionIconOpen : ''}`}>+</span>
      </button>
      {open && (
        <div className={css.answer}>
          {isHtml
            ? <div dangerouslySetInnerHTML={{ __html: a }} />
            : <p>{a}</p>
          }
        </div>
      )}
    </div>
  );
};

const FAQPage = () => (
  <Page
    title="FAQ | Buying & Selling on Dhobara"
    description="Answers to common questions about buying and selling pre-owned South Asian designer wear on Dhobara — shipping, returns, authenticity, pricing, and more."
    schema={faqSchema}
    scrollingDisabled={false}
  >
  <DhobaraPageLayout>
    <div className={css.pageHeader}>
      <span className={css.eyebrow}>Questions Answered</span>
      <h1 className={css.pageTitle}>Frequently Asked <em>Questions</em></h1>
      <p className={css.pageSubtitle}>Everything you need to know about buying and selling on Dhobara.</p>
    </div>
    <div className={css.content}>
      {FAQ_SECTIONS.map(section => (
        <div key={section.title} className={css.section}>
          <h2 className={css.sectionTitle}>{section.title}</h2>
          {section.items.map(item => (
            <FAQItem key={item.q} q={item.q} a={item.a} isHtml={item.isHtml} />
          ))}
        </div>
      ))}
    </div>
  </DhobaraPageLayout>
  </Page>
);

export default FAQPage;
